import React, { useState } from "react";
import {
  failureMessageConstant,
  keyConstants,
  regexConstant,
  successMessageConstant
} from "../../constants";
import { useForm } from "react-hook-form";
import Auth0Service from "../../services/auth0Service";
import { useDispatch, useSelector } from "react-redux";
import CryptoJS from "crypto-js";
import { Loader } from "../../common/components/loader";
import Utils from "../../utility";
import { BusinessUser } from "../../services";
import { removeUser } from "../../slices/userSlice";
import CustomTooltip from "../../common/components/customTooltip";
import PropTypes from "prop-types";

const { CHANGE_PASSWORD_SUCCESS } = successMessageConstant;
const {
  PASSWORD_REQUIRED,
  PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS,
  PASSWORD_MATCH_ERROR,
  CURRENT_PASSWORD_ERROR,
  NEW_PASSWORD_CHECKED,
  CHANGE_PASSWORD_FAILED
} = failureMessageConstant;

const ChangePasswordDialog = ({
  showChangePassword,
  setShowChangePassword
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch
  } = useForm();
  const user = useSelector((state) => state.user);
  const [loader, setLoader] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const oldPassword = watch("password");
  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  /**
   * Validates the current password.
   *
   * @param {string} password - The password to be validated.
   * @return {Promise<boolean>} - A promise that resolves to true if the password is valid, false otherwise.
   */
  const currentPasswordValidate = async (password) => {
    try {
      // Create an instance of Auth0Service
      const auth0Service = new Auth0Service();

      // Get the token using the user's email and password
      const result = await auth0Service.getToken({
        email: user?.email,
        password
      });

      // Check if there is an error in the response data
      if (result?.error || result?.response?.data?.error) {
        setLoader(false);
        throw new Error(CURRENT_PASSWORD_ERROR);
      }

      // Password is valid
      return true;
    } catch {
      setLoader(false);
      setErrMsg(CURRENT_PASSWORD_ERROR);
      return false;
    }
  };

  const logOutHandler = () => {
    dispatch(removeUser({}));
  };

  /**
   * Handles the submission of data.
   *
   * @param {Object} data - The data to be submitted.
   * @return {Promise<void>} A promise that resolves when the submission is successful.
   */
  const submitHandler = async (data) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot();
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    try {
      setLoader(true);
      setErrMsg("");
      const { password, newPassword } = data;

      // Check if the current password is valid
      const checkCurrentPassword = await currentPasswordValidate(password);

      // If current password is not valid, stop the submission
      if (!checkCurrentPassword) return;

      // Get secret key from environment variables
      const secretKey = process.env.REACT_APP_SECRET_KEY;

      // Encrypt the newPassword using AES encryption
      const encryptedNewPassword = CryptoJS.AES.encrypt(
        newPassword,
        secretKey
      ).toString();

      // Create payload object with userId and encrypted newPassword
      const payload = {
        userId: user.userId,
        newPassword: encryptedNewPassword
      };

      // Change the password by calling the changePassword method
      const responseResult = await new BusinessUser().changePassword(payload);

      // If responseResult is falsy, throw an error
      if (!responseResult) {
        throw Error;
      }

      Utils.successToastMessage(CHANGE_PASSWORD_SUCCESS);
      setLoader(false);

      // Log out the user
      logOutHandler();
    } catch {
      setLoader(false);
      Utils.failureToastMessage(CHANGE_PASSWORD_FAILED);
    }
  };

  /**
   * Handles the cancel action by resetting, clearing error message, and closing change password popup.
   *
   */
  const handleCancel = () => {
    reset();
    setErrMsg("");
    setShowChangePassword(false);
  };

  return (
    <div>
      {showChangePassword && (
        <div className="fixed inset-0 z-30 h-screen w-full bg-black-00000050 px-3">
          <div className="flex size-full items-center">
            <div className="m-auto mt-11 h-112.5 w-full rounded-[12px] bg-white xs:max-w-391px md:max-w-457px">
              <div className="m-auto min-h-112.5 w-full rounded-[10px] bg-white px-3 py-5 font-MontserratMedium text-ft15-26.25 text-black-30 xs:max-w-391px md:max-w-457px md:p-6">
                <div className="font-MontserratSemiBold text-ft22-26.82-0.4 text-black-20">
                  Change Password
                </div>
                <form onSubmit={handleSubmit(submitHandler)}>
                  <div className="mt-4.5">
                    Current Password
                    <div>
                      <div
                        className={`flex h-49px items-center justify-between rounded border ${errors?.password || errMsg ? "border-red-10" : "border-grey-D9D9D9"}  px-4 `}
                      >
                        <input
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          placeholder="Enter current password"
                          className="w-full font-MontserratRegular text-ft15-26.25 outline-none"
                          {...register("password", {
                            required: PASSWORD_REQUIRED
                          })}
                        />
                        {oldPassword && (
                          <img
                            onClick={() => setShowPassword(!showPassword)}
                            className="cursor-pointer"
                            alt="eye"
                            src={
                              showPassword
                                ? "/images/unmask.svg"
                                : "/images/mask.svg"
                            }
                          />
                        )}
                      </div>
                      {(!!errors?.password || errMsg) && (
                        <div className="mt-2 flex items-start gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                          <img src="/images/info-circle.svg" alt="info"></img>
                          <p>{errors?.password?.message || errMsg}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-5.5">
                    <p className="flex items-center gap-1">
                      New Password
                      <CustomTooltip
                        text={PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS}
                        positionLeft={14}
                      />
                    </p>
                    <div>
                      <div
                        className={`flex h-49px items-center justify-between rounded border ${errors?.newPassword ? "border-red-10" : "border-grey-D9D9D9"}  px-4 `}
                      >
                        <input
                          type={showNewPassword ? "text" : "password"}
                          autoComplete="new-password"
                          placeholder="Enter new password"
                          className="w-full font-MontserratRegular text-ft15-26.25 outline-none"
                          {...register("newPassword", {
                            required: PASSWORD_REQUIRED,
                            validate: (value) =>
                              value !== oldPassword || NEW_PASSWORD_CHECKED,
                            pattern: {
                              value: regexConstant.PASSWORD_REGEX,
                              message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                            },
                            minLength: {
                              value: 8,
                              message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                            }
                          })}
                        />
                        {newPassword && (
                          <img
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="cursor-pointer"
                            alt="eye"
                            src={
                              showNewPassword
                                ? "/images/unmask.svg"
                                : "/images/mask.svg"
                            }
                          />
                        )}
                      </div>
                      {!!errors?.newPassword && (
                        <div className="mt-2 flex items-start gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                          <img src="/images/info-circle.svg" alt="info"></img>
                          <p>{errors?.newPassword?.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-5.5">
                    <p className="flex items-center gap-1">
                      Confirm Password
                      <CustomTooltip
                        text={PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS}
                        positionLeft={-13}
                      />
                    </p>
                    <div>
                      <div
                        className={`flex h-49px items-center justify-between rounded border ${errors?.confirmPassword ? "border-red-10" : "border-grey-D9D9D9"}  px-4 `}
                      >
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          placeholder="Confirm new password"
                          className="w-full font-MontserratRegular text-ft15-26.25 outline-none"
                          {...register("confirmPassword", {
                            required: PASSWORD_REQUIRED,
                            validate: (value) =>
                              value === newPassword || PASSWORD_MATCH_ERROR,
                            pattern: {
                              value: regexConstant.PASSWORD_REGEX,
                              message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                            },
                            minLength: {
                              value: 8,
                              message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                            }
                          })}
                        />
                        {confirmPassword && (
                          <img
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="cursor-pointer"
                            alt="eye"
                            src={
                              showConfirmPassword
                                ? "/images/unmask.svg"
                                : "/images/mask.svg"
                            }
                          />
                        )}
                      </div>
                      {!!errors?.confirmPassword && (
                        <div className="mt-2 flex items-start gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                          <img src="/images/info-circle.svg" alt="info"></img>
                          <p>{errors?.confirmPassword?.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-35px flex items-center justify-between gap-8">
                    <button
                      onClick={handleCancel}
                      className="h-12 w-183px rounded border border-grey-D9D9D9"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={loader}
                      type="submit"
                      className="h-12 w-183px rounded bg-purple-6C36B0 text-white"
                    >
                      {loader ? <Loader /> : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ChangePasswordDialog.propTypes = {
  showChangePassword: PropTypes,
  setShowChangePassword: PropTypes
};
export default ChangePasswordDialog;
